import { toast } from "@devhop/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  CreateDepartmentFormDTO,
  UpdateDepartmentFormDTO,
} from "@/modules/departments/domain/contracts";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { departmentsApi } from "./client";

export const departmentsKeys = {
  all: ["departments"] as const,
  list: (q: Partial<OffsetPageQueryDTO>) => ["departments", "list", q] as const,
  detail: (id: string) => ["departments", "detail", id] as const,
  positions: (id: string, q: Partial<OffsetPageQueryDTO>) =>
    ["departments", "positions", id, q] as const,
};

export function useDepartmentsQuery(query: Partial<OffsetPageQueryDTO> = {}) {
  const q: OffsetPageQueryDTO = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
  };
  return useQuery({
    queryKey: departmentsKeys.list(q),
    queryFn: () => departmentsApi.list(q),
  });
}

export function useDepartmentQuery(id: string) {
  return useQuery({
    queryKey: departmentsKeys.detail(id),
    queryFn: () => departmentsApi.get(id),
    enabled: !!id,
  });
}

export function useDepartmentPositionsQuery(
  id: string,
  query: Partial<OffsetPageQueryDTO> = {},
) {
  const q: OffsetPageQueryDTO = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
  };
  return useQuery({
    queryKey: departmentsKeys.positions(id, q),
    queryFn: () => departmentsApi.getPositions(id, q),
    enabled: !!id,
  });
}

export function useCreateDepartment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateDepartmentFormDTO) =>
      departmentsApi.create(input),
    onSuccess: () => {
      toast.success("ສ້າງພະແນກສໍາເລັດ");
      qc.invalidateQueries({ queryKey: departmentsKeys.all });
    },
    onError: () => toast.error("ສ້າງພະແນກລົ້ມເຫຼວ"),
  });
}

export function useUpdateDepartment(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateDepartmentFormDTO) =>
      departmentsApi.update(id, input),
    onSuccess: () => {
      toast.success("ແກ້ໄຂພະແນກສໍາເລັດ");
      qc.invalidateQueries({ queryKey: departmentsKeys.detail(id) });
      qc.invalidateQueries({ queryKey: departmentsKeys.all });
    },
    onError: () => toast.error("ແກ້ໄຂພະແນກລົ້ມເຫຼວ"),
  });
}

export function useDeleteDepartment() {
  const qc = useQueryClient();
  const base = useMutation({
    mutationFn: (id: string) => departmentsApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: departmentsKeys.all });
    },
  });

  const run = (id: string) =>
    new Promise<void>((resolve, reject) => {
      base.mutate(id, {
        onSuccess: () => resolve(),
        onError: (e) => reject(e),
      });
    });

  return { ...base, run };
}
